/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-07-09 23:10:10
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-11-03 14:53:02
 */
import { useCallback } from 'react'
import { message, Modal } from 'antd'
import dayjs from 'dayjs'
import createExcelTask, {
  ExcelTaskType
} from '../../../app/libs/createExcelTask'
import useRectifyAnalytics from './useRectifyAnalytics'
import { RectifyRecord, RectifyDataState, FilterParams } from '../types'

// Hook 配置选项类型
interface UseRectifyActionsOptions {
  batchDelete: (params: { recordIDs: number[] }) => Promise<any>
  shareRecord: (params: any) => Promise<any>
  exportData: (params: any) => Promise<any>
  exportByIds: (params: any) => Promise<any>
  loadData: (params?: any) => Promise<any>
  resetSelection: () => void
  removeFromSelection: (id: number | string) => void
  selectedIds: (number | string)[]
  data: RectifyDataState
  filterParams: FilterParams
  groupId: string | number | null
  userId: string | number | null
}

/**
 * 管理整改记录操作的Hook
 * @param {Object} options - 配置选项
 * @param {Function} options.batchDelete - 批量删除API函数
 * @param {Function} options.shareRecord - 分享记录API函数
 * @param {Function} options.exportData - 导出数据API函数
 * @param {Function} options.exportByIds - 按ID导出数据API函数
 * @param {Function} options.loadData - 重新加载数据的函数
 * @param {Function} options.resetSelection - 重置选择的函数
 * @param {Function} options.removeFromSelection - 从选择中移除的函数
 * @param {Array} options.selectedIds - 当前选中的ID数组
 * @param {Object} options.data - 当前数据对象
 * @param {Object} options.filterParams - 当前筛选参数
 * @param {String|Number} options.groupId - 群组ID
 * @param {String|Number} options.userId - 用户ID
 * @returns {Object} 操作相关的方法
 */
const useRectifyActions = ({
  batchDelete,
  shareRecord,
  exportData,
  exportByIds,
  loadData,
  resetSelection,
  removeFromSelection,
  selectedIds,
  data,
  filterParams,
  groupId,
  userId
}: UseRectifyActionsOptions) => {
  // 使用埋点hook
  const { trackRectifyEvent } = useRectifyAnalytics()

  /**
   * 获取要删除的记录ID
   * @param {Object|null} record - 单个记录（如果是批量删除则为null）
   * @returns {Array} 要删除的记录ID数组
   */
  const getRecordIdsToDelete = useCallback(
    (record: RectifyRecord | null) => {
      if (record) {
        // 单个删除
        return [Number(record.recordID)]
      } else {
        // 批量删除所有选中的ID（跨页）
        return selectedIds.map((id) => Number(id))
      }
    },
    [selectedIds]
  )

  /**
   * 通用删除函数 - 处理单个和批量删除
   * @param {Object|null} record - 单个记录（如果是批量删除则为null）
   */
  const handleDelete = useCallback(
    async (record: RectifyRecord | null = null) => {
      // 获取要删除的记录ID
      const recordIDs = getRecordIdsToDelete(record)

      // 检查是否有选中的记录
      if (!record && recordIDs.length === 0) {
        message.warning('请先选择要删除的记录')
        return
      }

      // 设置成功消息
      const successMessage = record
        ? '删除成功'
        : `成功删除${recordIDs.length}条记录`

      try {
        // 上报删除确认埋点
        trackRectifyEvent('ConfirmDeleteRecord', data)

        // 调用批量删除API
        const response = await batchDelete({ recordIDs })

        if (response && response.status === 0) {
          message.success(successMessage)

          // 更新选中项
          if (record) {
            // 单个删除，从选中列表中移除
            removeFromSelection(record.recordID)
          } else {
            // 批量删除，重置选择状态
            resetSelection()
          }

          // 重新加载数据
          await loadData(filterParams)
        } else {
          if (response.status === -160) {
            trackRectifyEvent('showToastDelete', data)
            message.error('非管理员只能删除自己的整改记录，请先筛选自己的记录')
          } else {
            message.error(response?.msg || '删除失败')
          }
        }
      } catch (error) {
        console.error('删除失败:', error)
        message.error('删除失败')
      }
    },
    [
      batchDelete,
      getRecordIdsToDelete,
      filterParams,
      loadData,
      removeFromSelection,
      resetSelection,
      trackRectifyEvent,
      data
    ]
  )

  /**
   * 通用删除函数 - 带确认弹窗
   * @param {Object|null} record - 单个记录（如果是批量删除则为null）
   */
  const handleDeleteWithConfirm = useCallback(
    (record: RectifyRecord | null = null) => {
      // 获取要删除的记录ID
      const recordIDs = getRecordIdsToDelete(record)

      // 上报删除埋点
      trackRectifyEvent(record ? 'deleteRecord' : 'clickDelete', data)

      // 检查是否有选中的记录
      if (!record && recordIDs.length === 0) {
        message.warning('请先选择要删除的记录')
        return
      }

      trackRectifyEvent('showPop', data)
      // 显示确认对话框
      Modal.confirm({
        title: '是否删除整改记录？',
        okText: '删除',
        okType: 'danger',
        cancelText: '取消',
        onOk: () => {
          handleDelete(record)
        },
        onCancel: () => {
          // 上报取消删除埋点
          trackRectifyEvent('CancelDeleteRecord', data)
        }
      })
    },
    [getRecordIdsToDelete, handleDelete, trackRectifyEvent, data]
  )

  /**
   * 检查是否可以批量删除
   * @returns {Boolean} 是否可以批量删除
   */
  const canBatchDelete = useCallback(() => {
    // 只要有选中记录就允许点击删除按钮，权限判断交给后端
    return selectedIds.length > 0
  }, [selectedIds])

  /**
   * 处理分享
   * @param {Object} record - 要分享的记录
   */
  const handleShare = useCallback(
    async (record: RectifyRecord) => {
      trackRectifyEvent('shareRecord', data)
      try {
        // 调用分享API
        const response = await shareRecord({
          groupID: groupId,
          userID: userId,
          status: record.status,
          recordID: Number(record.recordID)
        })

        if (response && response.status === 0 && response.shareH5URI) {
          // 复制链接到剪贴板
          await copyToClipboard(
            `${response.shareH5URI}&fromPage=wechatShareAll`
          )
          message.info('整改链接已复制，可直接粘贴')

          // 上报分享埋点
          trackRectifyEvent('showToast', data)
        } else {
          message.error(response?.msg || '分享失败')
        }
      } catch (error) {
        console.error('分享失败:', error)
        message.error('分享失败，请稍后重试')
      }
    },
    [shareRecord, groupId, userId, trackRectifyEvent, data]
  )

  /**
   * 导出表格
   */
  const handleExport = useCallback(async () => {
    try {
      const baseParams = {
        ...filterParams,
        groupID: groupId,
        userID: userId
      }

      let response

      // 根据是否有选中记录决定调用哪个接口
      if (selectedIds.length > 0) {
        // 有选中记录，调用按ID导出的接口
        response = await exportByIds({
          ...baseParams,
          useRecordIDs: selectedIds.map((id) => Number(id))
        })
      } else {
        // 没有选中记录，调用普通导出接口
        response = await exportData(baseParams)
      }

      if (response && response.status === 0) {
        // 获取下载链接 - 根据接口返回格式调整
        const downloadUrl = response.exportURL
        if (downloadUrl) {
          // 创建导出任务记录
          createExcelTask({
            groupID: groupId as string | undefined,
            membersNum:
              selectedIds.length > 0 ? selectedIds.length : data.totalCount,
            exportType: ExcelTaskType.RECTIFICATION,
            startDate: filterParams.startTime || dayjs().format('YYYY-MM-DD'),
            endDate: filterParams.endTime || dayjs().format('YYYY-MM-DD'),
            finished: 1, // 0表示已完成，可以直接下载
            fileName: '整改记录表.xlsx',
            url: downloadUrl,
            contentNumber:
              selectedIds.length > 0 ? selectedIds.length : data.totalCount,
            fromPlace: 'rectify' // 添加来源标识
          })

          // 打开导出记录页面
          window.open('/attendance/export-list?fromPlace=rectifyRecords')
          message.success('导出成功')

          // 上报导出表格埋点
          trackRectifyEvent('clickSaveExcel', data, {
            startDate: filterParams.startTime,
            endDate: filterParams.endTime,
            sourceType:
              filterParams.status === 1
                ? 'unverify'
                : filterParams.status === 2
                ? 'verified'
                : 'all'
          })
        } else {
          message.error('导出失败：未获取到下载链接')
        }
      } else {
        // 处理特殊状态码
        if (response && response.exportResult !== undefined) {
          switch (response.exportResult) {
            case -1:
              message.warning('已有导出任务正在进行，请稍后再试')
              break
            case -2:
              message.warning('服务器繁忙，请稍后再试')
              break
            case -5:
              message.warning('导出数量超出限制')
              break
            case 0:
              message.error('服务器导出失败')
              break
            default:
              message.error(response?.msg || '导出失败')
              break
          }
        } else {
          message.error(response?.msg || '导出失败')
        }
      }
    } catch (error) {
      console.error('导出失败:', error)
      message.error('导出失败，请稍后重试')
    }
  }, [
    exportByIds,
    exportData,
    filterParams,
    groupId,
    userId,
    selectedIds,
    data,
    trackRectifyEvent
  ])

  /**
   * 下载记录
   */
  const handleDownload = useCallback(() => {
    // 直接跳转到下载记录页面
    window.open('/attendance/export-list?fromPlace=rectifyRecords')

    // 上报下载记录埋点
    trackRectifyEvent('clickDownloadRecord', data, {
      startDate: filterParams.startTime,
      endDate: filterParams.endTime,
      sourceType:
        filterParams.status === 1
          ? 'unverify'
          : filterParams.status === 2
          ? 'verified'
          : 'all'
    })
  }, [trackRectifyEvent, data, filterParams])

  /**
   * 发起整改
   */
  const handleRectify = useCallback(() => {
    // 上报发起整改埋点
    trackRectifyEvent('发起整改', data, {
      startDate: filterParams.startTime,
      endDate: filterParams.endTime,
      sourceType:
        filterParams.status === 1
          ? 'unverify'
          : filterParams.status === 2
          ? 'verified'
          : 'all'
    })
  }, [trackRectifyEvent, data, filterParams])

  /**
   * 复制文本到剪贴板
   * @param {string} text - 要复制的文本
   * @returns {Promise} - 复制操作的Promise
   */
  const copyToClipboard = useCallback((text: string) => {
    return new Promise<boolean>((resolve, reject) => {
      // 现代浏览器API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(text)
          .then(() => resolve(true))
          .catch((err) => {
            console.error('无法使用Clipboard API复制:', err)
            fallbackCopyToClipboard(text, resolve, reject)
          })
      } else {
        // 回退方法
        fallbackCopyToClipboard(text, resolve, reject)
      }
    })
  }, [])

  /**
   * 回退的复制到剪贴板方法（用于不支持Clipboard API的浏览器）
   * @param {string} text - 要复制的文本
   * @param {Function} resolve - Promise的resolve函数
   * @param {Function} reject - Promise的reject函数
   */
  const fallbackCopyToClipboard = useCallback((text: string, resolve: (value: boolean) => void, reject: (reason: Error) => void) => {
    try {
      // 创建临时textarea元素
      const textArea = document.createElement('textarea')
      textArea.value = text

      // 设置样式使其不可见
      textArea.style.position = 'fixed'
      textArea.style.top = '0'
      textArea.style.left = '0'
      textArea.style.width = '2em'
      textArea.style.height = '2em'
      textArea.style.padding = '0'
      textArea.style.border = 'none'
      textArea.style.outline = 'none'
      textArea.style.boxShadow = 'none'
      textArea.style.background = 'transparent'

      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      // 执行复制命令
      const successful = document.execCommand('copy')
      document.body.removeChild(textArea)

      if (successful) {
        resolve(true)
      } else {
        reject(new Error('复制失败'))
      }
    } catch (err) {
      reject(err as Error)
    }
  }, [])

  return {
    canBatchDelete,
    handleDelete,
    handleDeleteWithConfirm,
    handleShare,
    handleExport,
    handleDownload,
    handleRectify
  }
}

export default useRectifyActions
